import { Field, FieldProps, useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { BaseFormField } from 'types/form/baseFormField';
import { Dropdown, DropdownItem } from '../Dropdown';

export interface FormikDropdownListProps extends BaseFormField {
  placeholder?: string;
  items: DropdownItem[];
  disabled?: boolean;
  omitKeys?: (string | number)[]
  selectedItemKey?: string | number;
  onChange?: (selectedItem: DropdownItem | null) => void;
}

export const FormikDropdown: React.FC<FormikDropdownListProps> = ({
  name,
  items: propsItems,
  label,
  placeholder,
  required,
  disabled,
  selectedItemKey,
  onChange,
  omitKeys
}) => {
  const { setFieldValue, setFieldTouched, setFieldError, values } = useFormikContext();

  // Mémoïser les items filtrés
  const items = useMemo(() => {
    if (omitKeys) {
      return propsItems.filter((item) => !omitKeys.includes(item.key as any))
    }
    return propsItems
  }, [propsItems, omitKeys])

  // Récupérer la valeur actuelle du formulaire
  const currentValue = (values as any)[name];

  // Calculer selectedItem de manière dérivée au lieu d'utiliser useState.
  // Fallback par libellé : les données legacy stockent parfois le libellé au lieu
  // de la clé (ex. adresses en « France » vs code « FR ») — sans ce fallback, le
  // champ apparaît vide au préremplissage. La sauvegarde réécrit ensuite la clé.
  const selectedItem = useMemo(() => {
    const key = onChange ? selectedItemKey : currentValue;
    return items.find(item => item.key === key)
      ?? items.find(item => item.label === key)
      ?? null;
  }, [items, currentValue, selectedItemKey, onChange]);

  // Référence pour éviter les boucles
  const lastUpdatedValueRef = useRef(currentValue);

  // Quand la valeur a matché par libellé, normaliser la valeur formik vers la
  // clé : la validation et la sauvegarde attendent la clé (ex. 'FR'), pas le
  // libellé legacy. Ne boucle pas : après normalisation, key === currentValue.
  useEffect(() => {
    if (!onChange && selectedItem && selectedItem.key !== currentValue) {
      lastUpdatedValueRef.current = selectedItem.key;
      setFieldValue(name, selectedItem.key, false);
    }
  }, [onChange, selectedItem, currentValue, name, setFieldValue]);

  // Handler mémoïsé pour la sélection
  const handleSelect = useCallback((newSelectedItem: DropdownItem) => {
    if (onChange) {
      // Mode contrôlé
      onChange(newSelectedItem);
    } else {
      // Mode non contrôlé - mettre à jour Formik uniquement si la valeur change
      if (newSelectedItem.key !== lastUpdatedValueRef.current) {
        lastUpdatedValueRef.current = newSelectedItem.key;
        setFieldTouched(name, true, false);
        setFieldValue(name, newSelectedItem.key, false);
        setFieldError(name, undefined);
      }
    }
  }, [onChange, name, setFieldValue, setFieldTouched, setFieldError]);

  // Synchroniser la référence quand la valeur externe change
  useEffect(() => {
    lastUpdatedValueRef.current = currentValue;
  }, [currentValue]);

  return (
    <Field name={name}>
      {({ field, meta }: FieldProps) => (
        <Dropdown
          {...field}
          selectedItem={selectedItem}
          label={label}
          required={required}
          error={meta.touched && meta.error ? meta.error : ''}
          items={items}
          placeholder={placeholder}
          disabled={disabled}
          onSelect={handleSelect}
        />
      )}
    </Field>
  );
};
